import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import abstractImage from './blogimg/muscleRecoveryPostWorkout/abstract.jpeg';
import introductionImage from './blogimg/muscleRecoveryPostWorkout/introduction.jpeg';
import recoveryRitualsImage from './blogimg/muscleRecoveryPostWorkout/recovery-rituals.jpeg';
import hydrationImage from './blogimg/muscleRecoveryPostWorkout/hydration.jpeg';
import nutritionImage from './blogimg/muscleRecoveryPostWorkout/nutrition.jpeg';
import stretchingImage from './blogimg/muscleRecoveryPostWorkout/stretching.jpeg';
import sleepImage from './blogimg/muscleRecoveryPostWorkout/sleep.jpeg';
import supplementsImage from './blogimg/muscleRecoveryPostWorkout/supplements.jpg';
import wheyProteinImage from './blogimg/muscleRecoveryPostWorkout/whey-protein.jpeg';
import creatineImage from './blogimg/muscleRecoveryPostWorkout/creatine.jpeg';
import ashwagandhaImage from './blogimg/muscleRecoveryPostWorkout/ashwagandha.jpeg';
import lionsManeImage from './blogimg/muscleRecoveryPostWorkout/lions-mane.jpeg';
import recoveryRoutineImage from './blogimg/muscleRecoveryPostWorkout/recovery-routine.jpeg';

/**
 * Maps Firestore filenames to local image imports.
 */
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'abstract.jpg':
      return abstractImage;
    case 'introduction.jpg':
      return introductionImage;
    case 'recovery-rituals.jpg':
      return recoveryRitualsImage;
    case 'hydration.jpg':
      return hydrationImage;
    case 'nutrition.jpg':
      return nutritionImage;
    case 'stretching.jpg':
      return stretchingImage;
    case 'sleep.jpg':
      return sleepImage;
    case 'supplements.jpg':
      return supplementsImage;
    case 'whey-protein.jpg':
      return wheyProteinImage;
    case 'creatine.jpg':
      return creatineImage;
    case 'ashwagandha.jpg':
      return ashwagandhaImage;
    case 'lions-mane.jpg':
      return lionsManeImage;
    case 'recovery-routine.jpg':
      return recoveryRoutineImage;
    default:
      return null;
  }
}

const BlogMuscleRecoveryPostWorkoutPage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set document title
    document.title = "Muscle Recovery & Post-Workout Care: Evidence-Based Strategies";

    // Helper function to add/update meta tags
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Discover science-backed post-workout recovery tips, including hydration, nutrition, supplements, and sleep. Optimize muscle repair and overall fitness."
    );
    setMetaTag(
      "keywords",
      "muscle recovery, post-workout care, fitness, supplements, whey protein, creatine, ashwagandha, lions mane, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "Muscle Recovery & Post-Workout Care: Evidence-Based Tips",
      true
    );
    setMetaTag(
      "og:description",
      "Learn how hydration, nutrition, stretching, and targeted supplements can optimize your post-workout recovery for better performance and health.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/abstract.jpeg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/muscle-recovery-&-post-workout-care",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Cleanup on unmount (optional)
    return () => {
      document.title = "Pure Health Nexus";
    };
  }, []);

  // 2️⃣ Fetch Data from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const colRef = collection(db, 'products', 'blog', 'BlogMuscleRecoveryPostWorkout');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
          // console.warn('No blog document found for muscleRecoveryPostWorkout');
        }
      } catch (error) {
        // console.error('Error fetching blog article:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Organize Sections for Conclusion & References
  let conclusionSection = null;
  let referenceBlock = null;
  let otherBlocks = [];

  if (article.sections && article.sections.length > 0) {
    article.sections.forEach((section, sectionIndex) => {
      if (section.blocks && section.blocks.length > 0) {
        // If first block is 'h2' titled "Conclusion", treat that as conclusion
        if (section.blocks[0].tag === 'h2' && section.blocks[0].text === 'Conclusion') {
          conclusionSection = section;
        }
        // If it's the last section, treat it as references
        else if (sectionIndex === article.sections.length - 1) {
          referenceBlock = section.blocks;
        } else {
          otherBlocks.push(section);
        }
      }
    });
  }

  // 4️⃣ Render Block Function
  const renderBlock = (block, index, isReference = false) => {
    const { tag, text } = block;
    const style = isReference ? { fontSize: '10px', margin:'0' } : {};

    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1" style={style}>
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2" style={style}>
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3" style={style}>{text}</h3>
          </React.Fragment>
        );
      case 'p':
        return (
          <p key={index} className="recipe-p" style={style}>
            {text}
          </p>
        );
      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }
      case 'ul':
        return (
          <ul key={index} className="recipe-ul" style={style}>
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case "ol":
        return (
          <ol key={index} className="recipe-ol" style={style}>
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === "object") {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );
      default:
        return (
          <div key={index} style={style}>
            {text}
          </div>
        );
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  // 5️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {/* Main H1 from Firestore data */}
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}

        {/* Render other blocks (excluding Conclusion & References) */}
        {otherBlocks.map((section, sectionIndex) => (
          <section key={sectionIndex}>
            {section.blocks &&
              section.blocks.map((block, blockIndex) => renderBlock(block, blockIndex))}
          </section>
        ))}

        {/* Render Conclusion Section */}
        {conclusionSection && (
          <section>
            {conclusionSection.blocks.map((block, index) => renderBlock(block, index))}
          </section>
        )}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() =>
            (window.location.href = 'https://purehealthnexus.com/ashwagandha')
          }
        >
          See Top-Rated Ashwagandha Here
        </button>

        {/* Render References Section after the button */}
        {referenceBlock &&
          referenceBlock.map((block, index) => (
            <div className="notranslate" key={index}>
              {renderBlock(block, index, true)}
            </div>
          ))}
        </div>
    </div>
  );
};

export default BlogMuscleRecoveryPostWorkoutPage;
